"use client";

import { useId, useMemo, useRef, useState } from "react";
import { Button, Select as RACSelect, SelectValue, Tabs } from "react-aria-components";
import { useTranslations } from "next-intl";
import { clsx } from "clsx";

import IconChevronDownSmall from "@ui/central-icons/icons/IconChevronDownSmall";
import IconCrossLarge from "@ui/central-icons/icons/IconCrossLarge";
import IconPlusSmall from "@ui/central-icons/icons/IconPlusSmall";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { ListBox } from "@ui/components/core/ListBox";
import { TabList } from "@ui/components/core/TabList";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/components/core/Tooltip";

import { type PaygProductId, type PaygUsageInput, getPaygProduct, getPaygProductOptions } from "../payg-pricing-data";
import { type CalculatorMode, usePricingTableContext } from "../PricingTableContext";
import { BillingCycleToggle } from "../BillingCycleToggle";
import { getStepLabel, getUnitShort, sliderToValue, valueToSlider } from "./calculatorUtils";
import { UsageSlider } from "./UsageSlider";
import { StepIndicator } from "./StepIndicator";
import { ApiCalculatorResultCard } from "./ApiCalculatorResultCard";

export interface ApiCalculatorProps {
  className?: string;
}

interface ProductRow {
  id: string;
  productId: PaygProductId;
  usage: number;
}

interface ProductRowItemProps {
  row: ProductRow;
  usedProductIds: Set<PaygProductId>;
  isOnlyRow: boolean;
  onProductChange: (id: string, productId: PaygProductId) => void;
  onUsageChange: (id: string, usage: number) => void;
  onRemove: (id: string) => void;
}

function ProductRowItem({
  row,
  usedProductIds,
  isOnlyRow,
  onProductChange,
  onUsageChange,
  onRemove,
}: ProductRowItemProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  const { featureFlags } = usePricingTableContext();
  const product = getPaygProduct(row.productId, { featureFlags });
  const stepLabel = getStepLabel(t, product.unit);
  const unitShort = getUnitShort(t, product.unit);
  const sliderValue = valueToSlider(row.usage, product.maxValue);
  const productName = t(`products.${row.productId}` as Parameters<typeof t>[0]);
  const paygProductOptions = useMemo(() => getPaygProductOptions({ featureFlags }), [featureFlags]);

  // Hide products that are already chosen in *other* rows, but always keep this
  // row's current product so the dropdown isn't empty after a selection.
  const productItems = useMemo(
    () =>
      paygProductOptions
        .filter(p => p.id === row.productId || !usedProductIds.has(p.id))
        .map(p => ({
          id: p.id,
          textValue: t(`products.${p.id}` as Parameters<typeof t>[0]),
        })),
    [paygProductOptions, row.productId, usedProductIds, t],
  );

  return (
    <div className='tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-4 tw-space-y-4'>
      <div className='tw-flex tw-items-center tw-gap-2'>
        <RACSelect
          className='tw-flex-1 tw-min-w-0'
          selectedKey={row.productId}
          onSelectionChange={key => {
            if (key !== null && typeof key === "string") onProductChange(row.id, key as PaygProductId);
          }}
          aria-label={t("select_product")}
        >
          <Button
            className={({ isFocusVisible }) =>
              clsx(
                "tw-w-full tw-text-base tw-font-normal tw-text-black tw-flex tw-items-center tw-justify-between tw-gap-2 tw-max-h-11 tw-px-4 tw-py-3 tw-rounded-xl tw-bg-white tw-border tw-border-gray-200 hover:tw-border-gray-300 tw-transition-colors tw-cursor-pointer",
                isFocusVisible ? "tw-outline-focus" : "tw-outline-none focus:tw-border-black",
              )
            }
          >
            <SelectValue className='tw-flex-1 tw-text-left tw-truncate' />
            <IconChevronDownSmall className={clsx("tw-size-4 tw-text-gray-400 tw-flex-none", iconStrokeFix)} />
          </Button>
          <ListBox items={productItems} offset={8} />
        </RACSelect>
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                onClick={() => onRemove(row.id)}
                disabled={isOnlyRow}
                aria-label={t("remove_product_aria_label", { product: productName })}
                className='tw-flex tw-items-center tw-justify-center tw-size-10 tw-flex-none tw-rounded-full tw-text-gray-500 hover:tw-text-black hover:tw-bg-gray-100 tw-transition-colors disabled:tw-opacity-30 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-transparent disabled:hover:tw-text-gray-500 tw-outline-none focus-visible:tw-outline-focus'
              >
                <IconCrossLarge className={clsx("tw-size-4", iconStrokeFix)} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant='dark-new'>
              {t("remove_product_aria_label", { product: productName })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <UsageSlider
        sliderValue={sliderValue}
        onSliderChange={next => onUsageChange(row.id, sliderToValue(next, product.maxValue))}
        maxValue={product.maxValue}
        unitSuffix={unitShort}
        ariaLabel={`${productName} — ${stepLabel}`}
      />
    </div>
  );
}

export function ApiCalculator({ className }: ApiCalculatorProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  const { billingCycle, calculatorMode, setCalculatorMode, featureFlags } = usePricingTableContext();
  const paygProductOptions = useMemo(() => getPaygProductOptions({ featureFlags }), [featureFlags]);

  // useId + a counter keeps row IDs identical between SSR and hydration without paying for a
  // uuidv7() per row on the server. The initial row uses a literal suffix so the lazy initializer
  // stays pure (Strict Mode calls it twice).
  const rowIdPrefix = useId();
  const rowIdCounter = useRef(0);
  const makeRow = (productId: PaygProductId): ProductRow => {
    rowIdCounter.current += 1;
    return {
      id: `${rowIdPrefix}-${rowIdCounter.current}`,
      productId,
      usage: getPaygProduct(productId, { featureFlags }).defaultValue,
    };
  };

  const [rows, setRows] = useState<ProductRow[]>(() => [
    {
      id: `${rowIdPrefix}-initial`,
      productId: "tts_multilingual",
      usage: getPaygProduct("tts_multilingual", { featureFlags }).defaultValue,
    },
  ]);

  const usedProductIds = useMemo(() => new Set(rows.map(r => r.productId)), [rows]);

  const usages: PaygUsageInput[] = useMemo(
    () => rows.map(r => ({ product: getPaygProduct(r.productId, { featureFlags }), usage: r.usage })),
    [rows, featureFlags],
  );

  const handleProductChange = (id: string, productId: PaygProductId) => {
    setRows(prev =>
      prev.map(r =>
        r.id === id ? { ...r, productId, usage: getPaygProduct(productId, { featureFlags }).defaultValue } : r,
      ),
    );
  };

  const handleUsageChange = (id: string, usage: number) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, usage } : r)));
  };

  const handleRemoveRow = (id: string) => {
    setRows(prev => (prev.length <= 1 ? prev : prev.filter(r => r.id !== id)));
  };

  const handleAddRow = () => {
    setRows(prev => {
      const usedIds = new Set(prev.map(r => r.productId));
      const next = paygProductOptions.find(p => !usedIds.has(p.id));
      if (!next) return prev;
      return [...prev, makeRow(next.id)];
    });
  };

  const canAddRow = rows.length < paygProductOptions.length;

  const tabItems = [
    { id: "subscription", title: t("tab_subscription") },
    { id: "payg", title: t("tab_payg") },
  ];

  return (
    <div className={className}>
      <div className='tw-flex tw-flex-col tw-pt-4 lg:tw-pt-0 lg:tw-flex-row tw-gap-8 lg:tw-gap-16'>
        <div className='tw-w-full lg:tw-w-1/2 tw-space-y-8 tw-order-2 lg:tw-order-1'>
          <div>
            <StepIndicator step={1} label={t("step_payment_model")} />
            <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
              <Tabs selectedKey={calculatorMode} onSelectionChange={key => setCalculatorMode(key as CalculatorMode)}>
                <TabList items={tabItems} surface='cream' size='md' aria-label={t("payment_model_aria_label")} />
              </Tabs>
              {calculatorMode === "subscription" && <BillingCycleToggle size='md' />}
            </div>
          </div>

          <div>
            <StepIndicator step={2} label={t("step_products")} />
            <div className='tw-space-y-3'>
              {rows.map(row => (
                <ProductRowItem
                  key={row.id}
                  row={row}
                  usedProductIds={usedProductIds}
                  isOnlyRow={rows.length === 1}
                  onProductChange={handleProductChange}
                  onUsageChange={handleUsageChange}
                  onRemove={handleRemoveRow}
                />
              ))}
            </div>
            {canAddRow && (
              <button
                type='button'
                onClick={handleAddRow}
                className='tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-text-black hover:tw-border-gray-400 hover:tw-bg-gray-50 tw-transition-colors tw-type-sm tw-font-medium tw-outline-none focus-visible:tw-outline-focus'
              >
                <IconPlusSmall className={clsx("tw-size-4", iconStrokeFix)} />
                {t("add_product")}
              </button>
            )}
          </div>
        </div>

        <div className='tw-w-full lg:tw-w-1/2 tw-order-1 lg:tw-order-2'>
          <ApiCalculatorResultCard usages={usages} mode={calculatorMode} period={billingCycle} />
        </div>
      </div>
    </div>
  );
}
